import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FiAlertCircle, FiCheckCircle, FiLoader, FiZap } from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const MODE_LABELS = {
  finished: "finished-product",
  parts: "parts-based",
};

// Products per request. The server runs a batch at concurrency 5, so a chunk of
// 5 is roughly one AI round-trip — short enough to stay well inside any proxy
// timeout, and it gives the progress bar something real to count.
const CHUNK_SIZE = 5;

// Per-product outcome → how the results list labels it. `estimated` splits on
// assessment_type: a run that couldn't match anything still saves the
// packaging/transport draft, but Step 2 is blank, so it isn't a win.
function outcomeBadge(result) {
  if (result.status === "estimated") {
    if (result.assessment_type === "parts") {
      return { text: "Parts-based", className: "bg-success-subtle text-success border" };
    }
    if (result.assessment_type === "finished") {
      return { text: "Finished match", className: "bg-success-subtle text-success border" };
    }
    return { text: "No match", className: "bg-warning-subtle text-warning-emphasis border" };
  }
  if (result.status === "skipped_exists") {
    return { text: "Already assessed", className: "bg-light text-muted border" };
  }
  if (result.status === "needs_input") {
    return { text: "Needs data", className: "bg-warning-subtle text-warning-emphasis border" };
  }
  return { text: "Failed", className: "bg-danger-subtle text-danger border" };
}

/**
 * Bulk AI assessment for the products selected on the current page.
 *
 * The run is finished-product by default (one industry-average reference row
 * per product) with parts-based as the alternative (materials + a
 * manufacturing process each). Whichever mode runs, the products it couldn't
 * match come back tagged, and the modal offers to re-run just those in the
 * other mode rather than leaving the user to spot them in the table.
 *
 * Every product it assesses lands as a draft at Step 4 marked "Pending
 * approval" — nothing is published until the user opens and confirms it.
 */
function BulkAiAssessModal({
  show,
  items = [],
  excluded = {},
  maxPerRun,
  // Parts-based costs two AI calls per product, so it's a Team+ entitlement.
  // Without it the parts option renders locked: visible, greyed out, and
  // carrying its own route to the plans page.
  canUsePartsMode = false,
  onClose,
  onCompleted,
  getToken,
}) {
  // Snapshot the selection when the modal opens so a background refresh of the
  // product list can't change what's being assessed mid-run.
  const [queue, setQueue] = useState([]);
  const [mode, setMode] = useState("finished");
  const [overwrite, setOverwrite] = useState(false);
  const [phase, setPhase] = useState("setup"); // 'setup' | 'running' | 'results'
  const [runningMode, setRunningMode] = useState("finished");
  const [results, setResults] = useState([]);
  const [ranModes, setRanModes] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setQueue(items);
    setMode("finished");
    setOverwrite(false);
    setPhase("setup");
    setResults([]);
    setRanModes([]);
    setProgress({ done: 0, total: 0 });
    setError("");
    // Snapshot on open only — `items` changing underneath us is exactly what
    // this guards against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const nameById = new Map(queue.map((i) => [i.id, i]));
  // Products missing weight can't be estimated at all (it's the hard gate the
  // per-row action checks too), so they never leave the browser — no point
  // spending an AI call to be told the same thing.
  const blocked = queue.filter((i) => i.blocked);
  const runnable = queue.filter((i) => !i.blocked);
  const existingCount = runnable.filter((i) => i.hasExisting).length;
  const willSkipExisting = existingCount > 0 && !overwrite;
  const runnableCount = runnable.length - (willSkipExisting ? existingCount : 0);

  const busy = phase === "running";
  // Parts-based is a Team+ entitlement: the card renders locked (greyed out,
  // with its own "See plans" route) so the requirement is visible before anyone
  // picks it, and `mode` can never become 'parts' without the entitlement.
  const partsLocked = !canUsePartsMode;

  // Assess in small chunks rather than one long request: a 25-product run is
  // minutes of AI work, and a single request that long is at the mercy of every
  // proxy between here and the API. Each chunk's results land as they arrive,
  // so a failure part-way through keeps everything already assessed.
  const runAssessment = async (runMode, productIds, overwriteFlag) => {
    setPhase("running");
    setRunningMode(runMode);
    setError("");
    setProgress({ done: 0, total: productIds.length });

    let token;
    try {
      token = await getToken();
    } catch (err) {
      token = null;
    }
    if (!token) {
      setError("You must be logged in.");
      setPhase(results.length > 0 ? "results" : "setup");
      return;
    }

    const collected = [];
    let failure = "";
    for (let i = 0; i < productIds.length; i += CHUNK_SIZE) {
      const chunk = productIds.slice(i, i + CHUNK_SIZE);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/lca/auto-estimate`,
          { product_ids: chunk, overwrite: overwriteFlag, mode: runMode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        collected.push(...(response.data?.results || []));
        setProgress({ done: collected.length, total: productIds.length });
      } catch (err) {
        const data = err.response?.data;
        failure =
          data?.code === "PLAN_UPGRADE_REQUIRED"
            ? "AI assessment requires a paid plan."
            : data?.code === "PARTS_PLAN_UPGRADE_REQUIRED"
              ? "Parts-based AI assessments are available on the Team and Professional plans."
              : data?.error || "AI assessment failed. Please try again.";
        break;
      }
    }

    if (failure) {
      setError(
        collected.length > 0
          ? `${failure} ${collected.length} of ${productIds.length} products were assessed before it stopped — close this and run it again for the rest.`
          : failure
      );
    }

    if (collected.length > 0) {
      // A follow-up run replaces the earlier outcome for the products it re-ran.
      setResults((prev) => {
        const replaced = new Set(collected.map((r) => r.product_id));
        return [...prev.filter((r) => !replaced.has(r.product_id)), ...collected];
      });
      setRanModes((prev) => (prev.includes(runMode) ? prev : [...prev, runMode]));
      setPhase("results");
      if (onCompleted) onCompleted();
    } else {
      setPhase(results.length > 0 ? "results" : "setup");
    }
  };

  const handleRun = () => {
    const ids = runnable
      .filter((i) => overwrite || !i.hasExisting)
      .map((i) => i.id);
    // Nothing to send: say which of the two reasons it is and leave the fix
    // (the overwrite checkbox / adding a weight) on screen, rather than
    // greying the button out with no explanation.
    if (ids.length === 0) {
      setError(
        willSkipExisting && runnable.length > 0
          ? "Every product in this selection already has an assessment — tick \"Replace the existing assessment\" above to re-run them."
          : "None of the selected products have a weight yet, so there's nothing for the AI to work from. Add a weight on the product and run this again."
      );
      return;
    }
    setError("");
    runAssessment(mode, ids, overwrite);
  };

  // Products the run saved a draft for but couldn't build Step 2 for. The other
  // mode is a genuinely different route to a material assessment, so offer it
  // for exactly those products.
  const unmatched = results.filter(
    (r) => r.status === "estimated" && r.assessment_type === "packaging_transport_only"
  );
  const otherMode = ranModes[ranModes.length - 1] === "parts" ? "finished" : "parts";
  const canOfferOtherMode = unmatched.length > 0 && !ranModes.includes(otherMode);

  const counts = results.reduce(
    (acc, r) => {
      if (r.status === "estimated" && r.assessment_type !== "packaging_transport_only") acc.assessed += 1;
      else if (r.status === "estimated") acc.unmatched += 1;
      else if (r.status === "skipped_exists") acc.skipped += 1;
      else if (r.status === "needs_input") acc.needsData += 1;
      else acc.failed += 1;
      return acc;
    },
    { assessed: 0, unmatched: 0, skipped: 0, needsData: 0, failed: 0 }
  );

  // A locked card is greyed out and unselectable, but says why and carries the
  // way out (See plans) on its face — so the plan requirement is visible before
  // anyone tries to pick it, not after.
  const modeCard = (value, title, blurb, { locked = false, badge = null, lockedNote = null } = {}) => (
    <div
      className={`border rounded-3 p-3 flex-fill ${
        locked ? "bg-light" : mode === value ? "border-success bg-success-subtle" : "bg-white"
      }`}
      role={locked ? undefined : "button"}
      tabIndex={locked ? undefined : 0}
      onClick={locked ? undefined : () => setMode(value)}
      onKeyDown={
        locked
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMode(value);
              }
            }
      }
      style={{ cursor: locked ? "default" : "pointer" }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="radio"
            name="bulkAiMode"
            id={`bulkAiMode-${value}`}
            checked={!locked && mode === value}
            disabled={locked}
            onChange={() => setMode(value)}
          />
          <label
            className={`form-check-label fw-semibold ${locked ? "text-muted" : ""}`}
            htmlFor={`bulkAiMode-${value}`}
          >
            {title}
          </label>
          {badge && (
            <span className="badge bg-dark-subtle text-dark border ms-2" style={{ fontSize: 11 }}>
              {badge}
            </span>
          )}
        </div>
        {locked && (
          <Link
            to="/billing"
            className="btn btn-sm btn-dark text-nowrap"
            onClick={onClose}
          >
            See plans
          </Link>
        )}
      </div>
      <div className="small text-muted">{blurb}</div>
      {locked && lockedNote && <div className="small text-muted mt-2">{lockedNote}</div>}
    </div>
  );

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="bulkAiAssessModalTitle"
        style={{ display: "block", zIndex: 1055 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content" style={{ border: "none", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>
            <div
              className="modal-header border-0 pb-3"
              style={{ background: "linear-gradient(135deg, #f7fcf9 0%, #ffffff 100%)" }}
            >
              <h5
                className="modal-title d-flex align-items-center gap-2"
                id="bulkAiAssessModalTitle"
                style={{ fontWeight: 600, color: "#0f5132" }}
              >
                <FiZap size={20} />
                AI assess {queue.length} product{queue.length === 1 ? "" : "s"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={busy}
                style={{ opacity: busy ? 0.5 : 1 }}
              ></button>
            </div>

            <div className="modal-body" style={{ padding: "24px" }}>
              {error && (
                <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                  <FiAlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              {phase === "setup" && (
                <>
                  <p className="text-muted small">
                    Each product gets a draft assessment marked <em>Pending approval</em> — open it
                    in the calculator to review and confirm before anything is published.
                  </p>

                  <div className="d-flex gap-3 mb-3 flex-wrap">
                    {modeCard(
                      "finished",
                      "Finished product (recommended)",
                      "Matches each product to an industry-average finished product — fastest, and the right call for single-material items."
                    )}
                    {modeCard(
                      "parts",
                      "Parts-based",
                      "Breaks each product into its materials with a manufacturing process for each — better for multi-material products.",
                      {
                        locked: partsLocked,
                        badge: partsLocked ? "Team & Professional" : null,
                        lockedNote:
                          "Costs about twice as much to run, so it's on the Team and Professional plans. You can still build a parts assessment by hand in the calculator on any plan.",
                      }
                    )}
                  </div>

                  {existingCount > 0 && (
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="bulkAiOverwrite"
                        checked={overwrite}
                        onChange={(e) => setOverwrite(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="bulkAiOverwrite">
                        Replace the existing assessment on {existingCount} of these products
                        {overwrite ? "" : " (they'll be skipped otherwise)"}
                      </label>
                    </div>
                  )}

                  {blocked.length > 0 && (
                    <div className="alert alert-warning py-2 small">
                      <strong>{blocked.length} product{blocked.length === 1 ? "" : "s"}</strong> can't be
                      assessed yet — a weight is required. Add it on the product, then run this again.
                      <div className="text-muted mt-1">
                        {blocked.slice(0, 5).map((i) => i.name).join(", ")}
                        {blocked.length > 5 ? ` +${blocked.length - 5} more` : ""}
                      </div>
                    </div>
                  )}

                  {(excluded.overflow > 0 || excluded.linked > 0 || excluded.limitReached > 0) && (
                    <div className="alert alert-light border py-2 small mb-3">
                      {excluded.overflow > 0 && (
                        <div>
                          AI assessment runs {maxPerRun} products at a time — {excluded.overflow} of
                          your selection {excluded.overflow === 1 ? "isn't" : "aren't"} in this run.
                          Run it again for the rest.
                        </div>
                      )}
                      {excluded.linked > 0 && (
                        <div>
                          {excluded.linked} linked product{excluded.linked === 1 ? " was" : "s were"}{" "}
                          left out — copy {excluded.linked === 1 ? "it" : "them"} into your catalogue
                          from the row action first.
                        </div>
                      )}
                      {excluded.limitReached > 0 && (
                        <div>
                          {excluded.limitReached} product{excluded.limitReached === 1 ? " was" : "s were"}{" "}
                          left out — you've reached your plan's assessment limit.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {phase === "running" && (
                <div className="text-center py-4">
                  <FiLoader size={28} className="spinner text-success mb-3" />
                  <div className="fw-semibold">
                    Running the {MODE_LABELS[runningMode]} assessment…
                  </div>
                  <div className="text-muted small mt-1">
                    {progress.total > 0
                      ? `${progress.done} of ${progress.total} products done`
                      : "Starting…"}{" "}
                    — this usually takes a minute or two. Keep this window open.
                  </div>
                  {progress.total > 0 && (
                    <div className="progress mx-auto mt-3" style={{ height: 6, maxWidth: 320 }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                        aria-valuenow={progress.done}
                        aria-valuemin={0}
                        aria-valuemax={progress.total}
                      ></div>
                    </div>
                  )}
                </div>
              )}

              {phase === "results" && (
                <>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <FiCheckCircle className="text-success" />
                      <span>
                        <strong>{counts.assessed}</strong> assessed
                      </span>
                    </div>
                    {counts.unmatched > 0 && (
                      <span className="text-warning-emphasis">
                        <strong>{counts.unmatched}</strong> couldn't be matched
                      </span>
                    )}
                    {counts.skipped > 0 && (
                      <span className="text-muted">
                        <strong>{counts.skipped}</strong> already assessed
                      </span>
                    )}
                    {counts.needsData > 0 && (
                      <span className="text-warning-emphasis">
                        <strong>{counts.needsData}</strong> need data
                      </span>
                    )}
                    {counts.failed > 0 && (
                      <span className="text-danger">
                        <strong>{counts.failed}</strong> failed
                      </span>
                    )}
                  </div>

                  {canOfferOtherMode && (
                    <div className="alert alert-warning" role="alert">
                      <div className="fw-semibold mb-1">
                        {unmatched.length} product{unmatched.length === 1 ? "" : "s"} couldn't be
                        matched to a {MODE_LABELS[ranModes[ranModes.length - 1]]} assessment.
                      </div>
                      <div className="small mb-2">
                        Packaging and transport were still filled in, but the materials step is
                        empty. Would you like to run a {MODE_LABELS[otherMode]} assessment for{" "}
                        {unmatched.length === 1 ? "it" : `those ${unmatched.length}`}?
                      </div>
                      {otherMode === "parts" && partsLocked ? (
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <span className="small">
                            Parts-based AI assessments are on the <strong>Team</strong> and{" "}
                            <strong>Professional</strong> plans.
                          </span>
                          <Link to="/billing" className="btn btn-sm btn-dark" onClick={onClose}>
                            See plans
                          </Link>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() =>
                            runAssessment(otherMode, unmatched.map((r) => r.product_id), true)
                          }
                        >
                          Run {MODE_LABELS[otherMode]} for {unmatched.length} product
                          {unmatched.length === 1 ? "" : "s"}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="border rounded-3" style={{ maxHeight: 260, overflowY: "auto" }}>
                    <table className="table table-sm mb-0 align-middle">
                      <tbody>
                        {results.map((r) => {
                          const item = nameById.get(r.product_id);
                          const badge = outcomeBadge(r);
                          return (
                            <tr key={r.product_id}>
                              <td>
                                <div className="small fw-semibold">{item?.name || "Product"}</div>
                                {item?.code && <div className="text-muted" style={{ fontSize: 12 }}>{item.code}</div>}
                              </td>
                              <td className="text-end">
                                {r.status === "estimated" &&
                                  r.assessment_type !== "packaging_transport_only" &&
                                  r.total_emissions_kg != null && (
                                    <span className="text-muted small me-2">
                                      {Number(r.total_emissions_kg).toFixed(3)} kg CO₂e
                                    </span>
                                  )}
                                <span className={`badge ${badge.className}`}>{badge.text}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-muted small mt-3 mb-0">
                    Assessed products now show <em>Pending approval</em> in the list. Open each one to
                    review the estimate and complete it.
                  </p>
                </>
              )}
            </div>

            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={busy}
              >
                {phase === "results" ? "Done" : "Cancel"}
              </button>
              {phase !== "results" && (
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleRun}
                  disabled={busy}
                >
                  {busy ? (
                    <>
                      <FiLoader size={16} className="spinner" />
                      <span>Assessing…</span>
                    </>
                  ) : (
                    <>
                      <FiZap size={16} />
                      <span>
                        {runnableCount > 0
                          ? `Assess ${runnableCount} product${runnableCount === 1 ? "" : "s"}`
                          : "Assess products"}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        onClick={busy ? undefined : onClose}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
      ></div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { animation: spin 0.8s linear infinite; }
      `}</style>
    </>
  );
}

export default BulkAiAssessModal;
